<script lang="ts">
import type { Transaction } from '../../lib/types';
import { ProgressBar, EmptyState } from '../common';

type Props = {
  uncategorizedTransactions: Transaction[];
  isProcessing?: boolean;
  processingProgress?: { current: number; total: number };
  oncategorize?: (event: CustomEvent<{ transactionIds: string[] }>) => void;
};

let {
  uncategorizedTransactions = $bindable([]),
  isProcessing = $bindable(false),
  processingProgress = $bindable({ current: 0, total: 0 }),
  oncategorize
}: Props = $props();

// Selected transaction IDs
let selectedIds = $state<Set<string>>(new Set());

// Select/deselect all
let selectAll = $state(false);

// Filtering state
type FilterType = 'all' | 'uncategorized' | 'expenses' | 'income';
let activeFilter = $state<FilterType>('all');
let minAmount = $state<string>('');
let maxAmount = $state<string>('');

// Filtered transactions based on active filters
let filteredTransactions = $derived.by(() => {
  let filtered = uncategorizedTransactions;

  // Apply category filter
  if (activeFilter === 'uncategorized') {
    filtered = filtered.filter(t => !t.category || t.category === 'Uncategorized');
  } else if (activeFilter === 'expenses') {
    filtered = filtered.filter(t => t.amount < 0);
  } else if (activeFilter === 'income') {
    filtered = filtered.filter(t => t.amount > 0);
  }

  // Apply amount range filters
  const min = minAmount ? parseFloat(minAmount) : null;
  const max = maxAmount ? parseFloat(maxAmount) : null;

  if (min !== null) {
    filtered = filtered.filter(t => t.amount >= min);
  }
  if (max !== null) {
    filtered = filtered.filter(t => t.amount <= max);
  }

  return filtered;
});

// Handle select all toggle
function handleSelectAll() {
  if (selectAll) {
    selectedIds = new Set(filteredTransactions.map(t => t.id));
  } else {
    selectedIds = new Set();
  }
}

// Update select all state when filtered transactions change
$effect(() => {
  const filtered = filteredTransactions;
  if (filtered.length === 0) {
    selectAll = false;
  } else {
    selectAll = filtered.every(t => selectedIds.has(t.id));
  }
});

// Handle individual checkbox
function handleCheckbox(transactionId: string) {
  // Create a new Set to trigger reactivity properly
  const newSelectedIds = new Set(selectedIds);
  if (newSelectedIds.has(transactionId)) {
    newSelectedIds.delete(transactionId);
  } else {
    newSelectedIds.add(transactionId);
  }
  selectedIds = newSelectedIds;

  // Update select all state based on filtered transactions
  selectAll = filteredTransactions.length > 0 &&
    filteredTransactions.every(t => selectedIds.has(t.id));
}

// Handle categorize button
function handleCategorize() {
  console.log('[BulkCategorizationPanel] handleCategorize called', {
    selectedCount: selectedIds.size,
    selectedIds: Array.from(selectedIds)
  });

  if (selectedIds.size === 0) {
    console.log('[BulkCategorizationPanel] No items selected, returning');
    return;
  }

  console.log('[BulkCategorizationPanel] Calling oncategorize callback');

  // Call the callback prop directly (Svelte 5 pattern)
  if (oncategorize) {
    oncategorize(new CustomEvent('categorize', {
      detail: { transactionIds: Array.from(selectedIds) }
    }));
  }
}

// Computed values
let selectedCount = $derived(selectedIds.size);
let canCategorize = $derived(selectedCount > 0 && !isProcessing);
</script>

<div class="bulk-categorization-panel">
  <div class="panel-header">
    <h3>Bulk Categorization</h3>
    <p class="subtitle">
      Select transactions to categorize with AI in one batch
    </p>
  </div>

  {#if uncategorizedTransactions.length === 0}
    <EmptyState title="No uncategorized transactions found!" />
  {:else}
    <!-- Filter Controls -->
    <div class="filter-section">
      <div class="filter-buttons">
        <button
          class="filter-button"
          class:active={activeFilter === 'all'}
          onclick={() => activeFilter = 'all'}
          disabled={isProcessing}
        >
          All Transactions
        </button>
        <button
          class="filter-button"
          class:active={activeFilter === 'uncategorized'}
          onclick={() => activeFilter = 'uncategorized'}
          disabled={isProcessing}
        >
          Uncategorized
        </button>
        <button
          class="filter-button"
          class:active={activeFilter === 'expenses'}
          onclick={() => activeFilter = 'expenses'}
          disabled={isProcessing}
        >
          Expenses Only
        </button>
        <button
          class="filter-button"
          class:active={activeFilter === 'income'}
          onclick={() => activeFilter = 'income'}
          disabled={isProcessing}
        >
          Income Only
        </button>
      </div>

      <div class="amount-filters">
        <div class="amount-input-group">
          <label for="min-amount">Min Amount</label>
          <input
            id="min-amount"
            type="number"
            step="0.01"
            bind:value={minAmount}
            placeholder="-1000.00"
            disabled={isProcessing}
          />
        </div>
        <div class="amount-input-group">
          <label for="max-amount">Max Amount</label>
          <input
            id="max-amount"
            type="number"
            step="0.01"
            bind:value={maxAmount}
            placeholder="1000.00"
            disabled={isProcessing}
          />
        </div>
        {#if minAmount || maxAmount}
          <button
            class="clear-filters-button"
            onclick={() => { minAmount = ''; maxAmount = ''; }}
            disabled={isProcessing}
          >
            Clear
          </button>
        {/if}
      </div>
    </div>

    <div class="actions-bar">
      <label class="select-all-checkbox">
        <input
          type="checkbox"
          bind:checked={selectAll}
          onchange={handleSelectAll}
          disabled={isProcessing}
        />
        <span>Select All ({filteredTransactions.length})</span>
      </label>

      <button
        class="categorize-button"
        disabled={!canCategorize}
        onclick={handleCategorize}
      >
        {#if isProcessing}
          Processing... ({processingProgress.current}/{processingProgress.total})
        {:else}
          Categorize Selected ({selectedCount})
        {/if}
      </button>
    </div>

    {#if isProcessing}
      <ProgressBar
        current={processingProgress.current}
        total={processingProgress.total}
        text="Categorizing {processingProgress.current} of {processingProgress.total} transactions..."
      />
    {/if}

    <div class="transactions-list">
      {#if filteredTransactions.length === 0}
        <div class="no-results">
          <p>No transactions match the current filters.</p>
        </div>
      {:else}
        {#each filteredTransactions as transaction (transaction.id)}
          <label class="transaction-item" class:disabled={isProcessing}>
            <input
              type="checkbox"
              checked={selectedIds.has(transaction.id)}
              onchange={() => handleCheckbox(transaction.id)}
              disabled={isProcessing}
            />
            <div class="transaction-details">
              <div class="transaction-main">
                <span class="description">{transaction.description}</span>
                <span class="amount" class:negative={transaction.amount < 0}>
                  ${Math.abs(transaction.amount).toFixed(2)}
                </span>
              </div>
              <div class="transaction-meta">
                <span class="date">{transaction.date}</span>
                <span class="bank">{transaction.bank}</span>
                {#if transaction.category && transaction.category !== 'Uncategorized'}
                  <span class="category">{transaction.category}</span>
                {/if}
              </div>
            </div>
          </label>
        {/each}
      {/if}
    </div>
  {/if}
</div>

<style lang="scss">
.bulk-categorization-panel {
  background: var(--color-background-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--spacing-xl);
  margin-bottom: var(--spacing-xl);
}

.panel-header {
  margin-bottom: var(--spacing-lg);

  h3 {
    margin: 0 0 var(--spacing-xs) 0;
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
  }

  .subtitle {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
  }
}

.filter-section {
  margin-bottom: var(--spacing-lg);
}

.filter-buttons {
  display: flex;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
  flex-wrap: wrap;
}

.filter-button {
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-background);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-base) ease;

  &:hover:not(:disabled) {
    background: var(--color-background-tertiary);
    border-color: var(--color-primary);
  }

  &.active {
    background: var(--color-primary);
    color: white;
    border-color: var(--color-primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.amount-filters {
  display: flex;
  gap: var(--spacing-md);
  align-items: flex-end;
  flex-wrap: wrap;
}

.amount-input-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  flex: 1;
  min-width: 150px;

  label {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: var(--color-text-secondary);
  }

  input[type="number"] {
    padding: var(--spacing-sm);
    background: var(--color-background);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    font-size: var(--font-size-sm);
    color: var(--color-text-primary);
    transition: border-color 0.2s ease;

    &:focus {
      outline: none;
      border-color: var(--color-primary);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &::placeholder {
      color: var(--color-text-tertiary);
    }
  }
}

.clear-filters-button {
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-background-tertiary);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-base) ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: var(--color-background-secondary);
    color: var(--color-text-primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.actions-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-md);
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  margin-bottom: var(--spacing-lg);
}

.select-all-checkbox {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  cursor: pointer;
  user-select: none;

  input[type="checkbox"] {
    cursor: pointer;
  }

  span {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
  }

  &:has(input:disabled) {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.categorize-button {
  padding: var(--spacing-sm) var(--spacing-lg);
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-base) ease;

  &:not(:disabled):hover {
    background: color-mix(in srgb, var(--color-primary) 85%, black);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }

  &:not(:disabled):active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    background: var(--color-primary);
  }
}

.transactions-list {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-background);
}

.transaction-item {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  border-bottom: 1px solid var(--color-border);
  cursor: pointer;
  transition: background var(--transition-fast) ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover:not(.disabled) {
    background: var(--color-background-tertiary);
  }

  &.disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  input[type="checkbox"] {
    margin-top: 2px;
    cursor: pointer;
    flex-shrink: 0;
  }
}

.transaction-details {
  flex: 1;
  min-width: 0;
}

.transaction-main {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-xs);
}

.description {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.amount {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-success);
  flex-shrink: 0;

  &.negative {
    color: var(--color-text-primary);
  }
}

.transaction-meta {
  display: flex;
  gap: var(--spacing-md);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.date,
.bank {
  white-space: nowrap;
}

.category {
  padding: var(--spacing-xs) var(--spacing-sm);
  background: var(--color-primary-dim);
  color: var(--color-primary);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
}

.no-results {
  padding: var(--spacing-xl);
  text-align: center;
  color: var(--color-text-secondary);

  p {
    margin: 0;
    font-size: var(--font-size-sm);
  }
}
</style>
